(() => {
  'use strict';
  const SERVICE_ORIGIN = 'https://lunaria-assistant-private.onrender.com';
  const STORE_ORIGIN = 'https://lunarialight.com';
  const HANDOFF_KEY = 'lunaria:contact-handoff';
  const HANDOFF_TTL = 60 * 60 * 1000;
  const CONVERSATIONS_KEY = 'lunaria:assistant-conversations:v1';
  const CONVERSATIONS_TTL = 7 * 24 * 60 * 60 * 1000;
  const numericId = value => /^\d{1,20}$/.test(String(value || '')) ? String(value) : '';
  const cleanSummary = value => typeof value === 'string' ? value.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '').slice(0, 6500).trim() : '';
  const safeMessage = (event, frame) => Boolean(frame && event.source === frame.contentWindow && event.origin === SERVICE_ORIGIN && event.data && typeof event.data === 'object' && !Array.isArray(event.data));
  const captureStyles = (style, names) => names.map(name => [name, style.getPropertyValue(name), style.getPropertyPriority(name)]);
  const restoreStyles = (style, saved) => saved.forEach(([name, value, priority]) => {
    if (value) style.setProperty(name, value, priority);
    else style.removeProperty(name);
  });
  function restorePagePosition(position) {
    const style = document.documentElement.style;
    const saved = captureStyles(style, ['scroll-behavior']);
    style.setProperty('scroll-behavior', 'auto', 'important');
    try { window.scrollTo({ left: position.x, top: position.y, behavior: 'instant' }); }
    finally { restoreStyles(style, saved); }
  }
  function validHandoff(value, now = Date.now()) {
    return Boolean(value && value.version === 1 && Number.isFinite(value.at) && now >= value.at && now - value.at <= HANDOFF_TTL && numericId(value.productId) && numericId(value.variantId) && cleanSummary(value.summary));
  }
  function cleanSnapshot(value, now = Date.now()) {
    if (!value || value.version !== 1 || !Number.isFinite(value.savedAt) || value.savedAt > now || now - value.savedAt > CONVERSATIONS_TTL) return null;
    if (typeof value.contextStamp !== 'string' || value.contextStamp.length > 1000 || !Array.isArray(value.transcript) || value.transcript.length > 24) return null;
    if (value.conversationId !== null && (typeof value.conversationId !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.conversationId))) return null;
    const transcript = [];
    for (const item of value.transcript) {
      if (!item || !['user', 'assistant'].includes(item.role) || typeof item.content !== 'string' || item.content.length > (item.role === 'user' ? 1000 : 4000) || typeof item.reliable !== 'boolean' || typeof item.error !== 'boolean') return null;
      const feedback = item.feedback == null ? null : item.feedback;
      if (![null, 'helpful', 'not-helpful'].includes(feedback) || (item.role !== 'assistant' && feedback !== null)) return null;
      transcript.push({ role: item.role, content: item.content, reliable: item.reliable, error: item.error,
        ...(item.role === 'assistant' ? { feedback,
          ...(!item.error && Array.isArray(item.documentTypes) ? { documentTypes: ['guide', 'spec_sheet'].filter(type => item.documentTypes.includes(type)) } : {}) } : {}) });
    }
    const result = { version: 1, savedAt: value.savedAt, contextStamp: value.contextStamp, conversationId: value.conversationId, transcript };
    return JSON.stringify(result).length <= 100000 ? result : null;
  }
  function readConversations(storage, now = Date.now()) {
    try {
      const raw = storage.getItem(CONVERSATIONS_KEY);
      let parsed = {};
      if (raw && raw.length <= 180000) {
        try { parsed = JSON.parse(raw); } catch (_) {}
      }
      const entries = {};
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        Object.entries(parsed).filter(([key]) => /^\d{1,20}:\d{1,20}$/.test(key)).map(([key, value]) => [key, cleanSnapshot(value, now)]).filter(([, value]) => value).sort((a, b) => b[1].savedAt - a[1].savedAt).slice(0, 8).forEach(([key, value]) => { entries[key] = value; });
      }
      storage.setItem(CONVERSATIONS_KEY, JSON.stringify(entries));
      return { entries, available: true };
    } catch (_) { return { entries: {}, available: false }; }
  }
  function saveConversation(storage, key, value) {
    if (!/^\d{1,20}:\d{1,20}$/.test(key)) return false;
    const result = readConversations(storage);
    if (!result.available) return false;
    if (value === null) delete result.entries[key];
    else {
      const snapshot = cleanSnapshot(value);
      if (!snapshot) return false;
      result.entries[key] = snapshot;
    }
    let entries = Object.entries(result.entries).sort((a, b) => b[1].savedAt - a[1].savedAt || (a[0] === key ? -1 : b[0] === key ? 1 : 0)).slice(0, 8);
    while (JSON.stringify(Object.fromEntries(entries)).length > 180000) entries.pop();
    try { storage.setItem(CONVERSATIONS_KEY, JSON.stringify(Object.fromEntries(entries))); return true; } catch (_) { return false; }
  }
  function documentClickPayload(data, context, links) {
    if (!data || !context || !numericId(context.productId) || !numericId(context.variantId)
      || data.productId !== context.productId || data.variantId !== context.variantId
      || !['guide', 'spec_sheet'].includes(data.documentType)
      || !/^[a-f0-9]{12}$/.test(data.guideVersion || '')
      || !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(data.eventId || '')) return null;
    const link = [...links].find(item => !item.hasAttribute('data-guide-preview')
      && item.dataset.productId === context.productId
      && item.dataset.documentType === data.documentType
      && item.dataset.guideVersion === data.guideVersion
      && /^[a-z0-9-]{1,200}$/.test(item.dataset.productHandle || ''));
    if (!link) return null;
    return { event_id: data.eventId, product_id: context.productId,
      product_handle: link.dataset.productHandle, guide_version: data.guideVersion, document_type: data.documentType };
  }
  if (typeof module === 'object' && module.exports) {
    module.exports = { SERVICE_ORIGIN, STORE_ORIGIN, HANDOFF_KEY, HANDOFF_TTL, CONVERSATIONS_KEY, CONVERSATIONS_TTL, numericId, cleanSummary, safeMessage, validHandoff, cleanSnapshot, readConversations, saveConversation, documentClickPayload };
  }
  if (typeof window === 'undefined' || !window.customElements) return;

  class LunariaAssistantLauncher extends HTMLElement {
    connectedCallback() {
      if (this.initialised || this.closest('product-card, quick-add-dialog')) return;
      this.initialised = true;
      this.generation = 0;
      this.pendingVariant = false;
      this.dialog = null;
      this.frame = null;
      this.ready = false;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.scope = this.closest('.shopify-section') || this.closest('main');
      this.querySelector('.lnr-assistant__interactive').hidden = false;
      this.querySelectorAll('[data-assistant-question]').forEach(button => {
        button.addEventListener('click', () => this.open(button.textContent.trim()), options);
      });
      this.querySelector('form').addEventListener('submit', event => {
        event.preventDefault();
        this.open(this.querySelector('input[name="question"]').value.trim().slice(0, 1000));
      }, options);
      window.addEventListener('message', event => this.receive(event), options);
      document.addEventListener('shopify:product:select', event => this.variantSelected(event), options);
    }
    disconnectedCallback() {
      this.close({ immediate: true });
      this.abort?.abort();
      this.initialised = false;
    }
    currentContext() {
      const productId = numericId(this.dataset.productId);
      const forms = this.scope?.querySelectorAll('product-form-component[data-product-id]') || [];
      const form = [...forms].find(item => item.dataset.productId === productId && !item.closest('product-card, quick-add-dialog'));
      const variantId = numericId(form?.querySelector('input[name="id"]')?.value || this.dataset.variantId);
      return { productId, variantId };
    }
    updateStock(available) {
      const productId = numericId(this.dataset.productId);
      const stock = this.scope?.querySelector(`[data-assistant-stock][data-product-id="${productId}"]`) || this.querySelector('[data-assistant-stock]');
      if (!stock) return;
      stock.hidden = available !== true;
      stock.closest('.lnr-pt__row')?.classList.toggle('lnr-pt__row--3', available === true);
    }
    async variantSelected(event) {
      if (!event.target?.closest || event.target.closest('product-card, quick-add-dialog')) return;
      if (!this.scope?.contains(event.target) || !event.promise || typeof event.promise.then !== 'function') return;
      const generation = ++this.generation;
      this.pendingVariant = true;
      // La tuile stock garde son etat pendant la resolution de la variante : la masquer
      // ici faisait disparaitre "Ready to ship" et repasser la rangee de 3 a 2 colonnes,
      // donc un saut de mise en page a chaque changement de couleur. L'etat reel est
      // applique plus bas par updateStock() des que la variante repond.
      this.querySelectorAll('button').forEach(button => { button.disabled = true; });
      try {
        const result = await event.promise;
        if (!this.isConnected || generation !== this.generation) return;
        const detail = result?.detail;
        if (!detail || (detail.productId && String(detail.productId) !== this.dataset.productId)) return;
        const variantId = numericId(detail.resource?.id);
        this.dataset.variantId = variantId;
        this.pendingVariant = !variantId;
        this.updateStock(Boolean(variantId) && detail.resource?.available === true);
        if (variantId && this.ready) this.sendContext(undefined, variantId);
      } catch (_) {
        if (generation === this.generation) this.pendingVariant = true;
      } finally {
        if (generation === this.generation) this.querySelectorAll('button').forEach(button => { button.disabled = this.pendingVariant; });
      }
    }
    sendContext(question, resolvedVariantId) {
      if (!this.ready || !this.frame || this.pendingVariant || this.closing) return;
      const context = this.currentContext();
      if (resolvedVariantId) context.variantId = resolvedVariantId;
      if (!context.productId || !context.variantId) return;
      let saved = { entries: {}, available: false };
      try { saved = readConversations(localStorage); } catch (_) {}
      this.frame.contentWindow.postMessage({ type: 'lunaria:context', ...context, savedConversation: saved.entries[`${context.productId}:${context.variantId}`] || null, storageAvailable: saved.available, ...(question ? { question: question.slice(0, 1000) } : {}) }, SERVICE_ORIGIN);
    }
    open(question = '') {
      if (this.pendingVariant || !this.currentContext().variantId) return;
      if (this.dialog?.open) return;
      this.scrollRestoreVersion = (this.scrollRestoreVersion || 0) + 1;
      if (this.scrollRestoreFrame != null) window.cancelAnimationFrame(this.scrollRestoreFrame);
      this.scrollRestoreFrame = null;
      this.scrollLock = { x: window.scrollX, y: window.scrollY,
        body: captureStyles(document.body.style, ['position', 'top', 'left', 'width', 'overflow', 'padding-right', 'box-sizing']) };
      const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const paddingRight = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
      this.pendingQuestion = question.slice(0, 1000);
      this.returnFocus = document.activeElement;
      this.opened = false;
      const dialog = document.createElement('dialog');
      dialog.className = 'lnr-assistant-dialog';
      dialog.setAttribute('aria-label', this.dataset.dialogLabel);
      dialog.setAttribute('aria-busy', 'true');
      this.dialog = dialog;
      const fallback = document.createElement('div');
      fallback.className = 'lnr-assistant-dialog__fallback';
      fallback.setAttribute('role', 'status');
      const status = document.createElement('p');
      status.textContent = this.dataset.loadingLabel;
      const close = document.createElement('button');
      close.type = 'button';
      close.textContent = this.dataset.closeLabel;
      close.addEventListener('click', () => this.close());
      const contact = document.createElement('a');
      contact.href = '/pages/contact';
      contact.textContent = this.dataset.contactLabel;
      fallback.append(status, contact, close);
      this.fallback = fallback;
      if (location.origin === STORE_ORIGIN) {
        const iframe = document.createElement('iframe');
        iframe.title = this.dataset.dialogLabel;
        iframe.setAttribute('aria-hidden', 'true');
        iframe.setAttribute('referrerpolicy', 'strict-origin');
        iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads');
        iframe.src = `${SERVICE_ORIGIN}/storefront?parentOrigin=${encodeURIComponent(location.origin)}`;
        this.frame = iframe;
        dialog.append(iframe);
      } else {
        status.textContent = this.dataset.unavailableLabel;
      }
      dialog.append(fallback);
      dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      document.body.append(dialog);
      for (const [name, value] of Object.entries({ position: 'fixed', top: `${-this.scrollLock.y}px`,
        left: `${-this.scrollLock.x}px`, width: '100%', overflow: 'hidden',
        'padding-right': `${paddingRight + scrollbar}px`, 'box-sizing': 'border-box' })) {
        document.body.style.setProperty(name, value, 'important');
      }
      dialog.showModal();
      close.focus({ preventScroll: true });
      this.timeout = setTimeout(() => { status.textContent = this.dataset.unavailableLabel; }, 18000);
    }
    receive(event) {
      if (!this.dialog?.open || this.closing || !safeMessage(event, this.frame)) return;
      const data = event.data;
      if (data.type === 'lunaria:ready') {
        if (this.ready) return;
        this.ready = true;
        this.sendContext(this.pendingQuestion);
        this.pendingQuestion = '';
      } else if (data.type === 'lunaria:opened' && this.ready && !this.opened) {
        this.opened = true;
        clearTimeout(this.timeout);
        this.fallback.hidden = true;
        this.fallback.setAttribute('aria-hidden', 'true');
        this.dialog.setAttribute('aria-busy', 'false');
        this.dialog.classList.add('is-ready');
        this.frame.removeAttribute('aria-hidden');
        this.frame.focus({ preventScroll: true });
      } else if (data.type === 'lunaria:close') {
        this.close();
      } else if (data.type === 'lunaria:persist' && this.ready && !this.pendingVariant) {
        const context = this.currentContext();
        if (String(data.productId) !== context.productId || String(data.variantId) !== context.variantId) return;
        let storageAvailable = false;
        try { storageAvailable = saveConversation(localStorage, `${context.productId}:${context.variantId}`, data.snapshot); } catch (_) {}
        this.frame.contentWindow.postMessage({ type: 'lunaria:persisted', ...context, storageAvailable }, SERVICE_ORIGIN);
      } else if (data.type === 'lunaria:document-click' && this.ready && this.opened && !this.pendingVariant) {
        this.measureDocumentClick(data);
      } else if (data.type === 'lunaria:contact' && this.ready && !this.pendingVariant) {
        const context = this.currentContext();
        if (String(data.productId) !== context.productId || String(data.variantId) !== context.variantId) return;
        const summary = cleanSummary(data.summary);
        if (!summary) return;
        const record = { version: 1, at: Date.now(), ...context, summary };
        try {
          sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(record));
          location.assign('/pages/contact');
        } catch (_) {
          this.dialog.classList.remove('is-ready');
          this.frame.setAttribute('aria-hidden', 'true');
          this.fallback.removeAttribute('aria-hidden');
          this.fallback.hidden = false;
          this.fallback.querySelector('p').textContent = this.dataset.unavailableLabel;
          const field = document.createElement('textarea');
          field.value = summary;
          field.readOnly = true;
          field.setAttribute('aria-label', this.dataset.contactLabel);
          this.fallback.prepend(field);
          field.focus({ preventScroll: true });
          field.select();
        }
      }
    }
    measureDocumentClick(data) {
      try {
        if (location.origin !== STORE_ORIGIN || window.Shopify?.designMode || window.Shopify?.theme?.role !== 'main'
          || window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
        const payload = documentClickPayload(data, this.currentContext(), this.scope?.querySelectorAll('a[data-lnr-guide]') || []);
        if (!payload) return;
        this.documentClicks ||= new Map();
        const key = `${payload.product_id}:${payload.document_type}`;
        const previous = this.documentClicks.get(key);
        const now = Date.now();
        if (previous && (previous.eventId === payload.event_id || now - previous.at < 800)) return;
        this.documentClicks.set(key, { at: now, eventId: payload.event_id });
        void fetch(`${SERVICE_ORIGIN}/api/storefront/guide-click`, {
          method: 'POST', headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: JSON.stringify(payload), credentials: 'omit', referrerPolicy: 'no-referrer', keepalive: true
        }).catch(() => {});
      } catch (_) {}
    }
    close({ immediate = false } = {}) {
      clearTimeout(this.timeout);
      const dialog = this.dialog;
      if (!dialog) return;
      if (immediate) return this.finishClose(dialog);
      if (this.closing) return;
      const frame = this.frame;
      if (!dialog.open || !frame || !dialog.classList.contains('is-ready')
        || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return this.finishClose(dialog);
      const closing = { dialog, frame };
      this.closing = closing;
      const transform = window.getComputedStyle(frame).transform;
      frame.style.setProperty('--lnr-assistant-close-from', transform === 'none' ? 'translate3d(0,0,0)' : transform);
      frame.inert = true;
      const finish = () => {
        if (this.closing === closing && this.dialog === dialog) this.finishClose(dialog);
      };
      closing.onAnimationEnd = event => {
        if (event.target === frame && ['lnr-assistant-content-drawer-out', 'lnr-assistant-content-sheet-out'].includes(event.animationName)) finish();
      };
      frame.addEventListener('animationend', closing.onAnimationEnd);
      frame.addEventListener('animationcancel', closing.onAnimationEnd);
      closing.timeout = setTimeout(finish, 220);
      dialog.classList.add('is-closing');
    }
    finishClose(dialog) {
      if (this.dialog !== dialog) return;
      clearTimeout(this.timeout);
      if (this.closing) {
        clearTimeout(this.closing.timeout);
        this.closing.frame.removeEventListener('animationend', this.closing.onAnimationEnd);
        this.closing.frame.removeEventListener('animationcancel', this.closing.onAnimationEnd);
        this.closing = null;
      }
      const position = this.scrollLock;
      const returnFocus = this.returnFocus;
      this.ready = false;
      this.opened = false;
      this.dialog = null;
      this.frame = null;
      this.scrollLock = null;
      this.returnFocus = null;
      if (position) restoreStyles(document.body.style, position.body);
      dialog.close();
      dialog.remove();
      if (returnFocus?.isConnected && !returnFocus.disabled) returnFocus.focus({ preventScroll: true });
      if (position) {
        restorePagePosition(position);
        const version = this.scrollRestoreVersion;
        this.scrollRestoreFrame = window.requestAnimationFrame(() => {
          if (version !== this.scrollRestoreVersion) return;
          this.scrollRestoreFrame = null;
          if (this.dialog) return;
          restorePagePosition(position);
        });
      }
    }
  }
  if (!customElements.get('lunaria-assistant-launcher')) customElements.define('lunaria-assistant-launcher', LunariaAssistantLauncher);

  function prepareContact() {
    const config = document.querySelector('[data-lunaria-contact-config]');
    const field = document.querySelector('textarea[name="contact[body]"]');
    if (!config || !field || location.pathname.replace(/\/$/, '') !== '/pages/contact') return;
    let record;
    try { record = JSON.parse(sessionStorage.getItem(HANDOFF_KEY) || 'null'); } catch (_) { return; }
    if (!validHandoff(record)) {
      try { sessionStorage.removeItem(HANDOFF_KEY); } catch (_) {}
      return;
    }
    const summary = cleanSummary(record.summary);
    const insert = () => {
      field.value = field.value.trim() ? `${field.value.trim()}\n\n${summary}` : summary;
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
      try { sessionStorage.removeItem(HANDOFF_KEY); } catch (_) {}
      const note = document.createElement('p');
      note.className = 'lnr-assistant-handoff-note';
      note.setAttribute('role', 'status');
      note.textContent = config.dataset.addedLabel;
      field.after(note);
    };
    if (!field.value.trim()) insert();
    else {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lnr-assistant-handoff';
      button.textContent = config.dataset.insertLabel;
      button.addEventListener('click', () => { insert(); button.remove(); }, { once: true });
      field.after(button);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', prepareContact, { once: true });
  else prepareContact();
})();
